// ClubOwnerDashboard.jsx
// Main dashboard component for Club Owners/Managers/Creators

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlus, FaUserPlus, FaBullhorn, FaCog, FaSyncAlt, 
  FaChevronDown, FaCheck, FaCrown, FaExternalLinkAlt 
} from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import { useNotification } from '../../../context/NotificationContext';
import ActionQueue from './ActionQueue';
import ClubMembershipOverview from './ClubMembershipOverview';
import ProgramStaffMatrix from './ProgramStaffMatrix';
import ProgramHealthCards from './ProgramHealthCards';
import EquipmentInventory from './EquipmentInventory';
import RecentAwardsWidget from './RecentAwardsWidget';
import ClubGrowthWidget from './ClubGrowthWidget';
import styles from './ClubOwnerDashboard.module.css';

const ClubOwnerDashboard = ({
  userClubs,
  activeMode,
  onModeChange,
  onExploreClick
}) => {
  const navigate = useNavigate();
  const { user, refreshUserClubs } = useUser();
  const { showSuccess, showError } = useNotification();
  
  // Get managed clubs (Creator, Owner, Manager roles) - memoized to prevent useEffect dependency changes
  const managedClubs = React.useMemo(() => {
    return userClubs?.filter(m => 
      ['Creator', 'Owner', 'Manager', 'Admin', 'creator', 'owner', 'manager', 'admin'].includes(m.role)
    ) || [];
  }, [userClubs]);
  
  // Selected club state - initialize from localStorage if available
  const [selectedClubId, setSelectedClubId] = useState(() => {
    const saved = localStorage.getItem('clubOwnerDashboard_selectedClub');
    return saved || null;
  });
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    club: null,
    programs: [],
    members: [],
    pendingClubApprovals: 0,
    pendingProgramApps: 0,
    pendingTeamInvites: 0,
    issues: 0,
    loading: true
  });

  // Initialize selected club - use saved value or first club
  useEffect(() => {
    if (managedClubs.length === 0) return;
    
    const savedClubId = localStorage.getItem('clubOwnerDashboard_selectedClub');
    
    // Check if saved club is still valid (user still manages it)
    const savedClubIsValid = savedClubId && managedClubs.some(m => {
      const clubId = m.club?._id || m.club;
      return String(clubId) === String(savedClubId);
    });
    
    if (savedClubIsValid) {
      setSelectedClubId(savedClubId);
    } else if (!selectedClubId) {
      // Fallback to first club if saved is invalid
      const firstClub = managedClubs[0]?.club;
      const firstClubId = firstClub?._id || firstClub;
      setSelectedClubId(firstClubId);
      localStorage.setItem('clubOwnerDashboard_selectedClub', firstClubId);
    }
  }, [managedClubs, selectedClubId]);

  // Get selected club data
  const selectedClubMembership = managedClubs.find(m => {
    const clubId = m.club?._id || m.club;
    return String(clubId) === String(selectedClubId);
  });
  const selectedClub = selectedClubMembership?.club;
  const userRole = selectedClubMembership?.role;

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!selectedClubId || !user) return;
    
    try {
      setDashboardData(prev => ({ ...prev, loading: true }));
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      
      // Get auth token
      const token = await user.getIdToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch club details
      const clubRes = await fetch(`${backendUrl}/api/clubs/${selectedClubId}`, { headers });
      const clubData = clubRes.ok ? await clubRes.json() : null;
      
      // Fetch programs
      const programsRes = await fetch(`${backendUrl}/api/programs/club/${selectedClubId}`, { headers });
      const programsData = programsRes.ok ? await programsRes.json() : [];
      
      // Fetch club members
      const membersRes = await fetch(`${backendUrl}/api/clubs/${selectedClubId}/members`, { headers });
      const membersData = membersRes.ok ? await membersRes.json() : { members: [] };
      const members = membersData.members || membersData || [];
      
      // Calculate pending club approvals
      const pendingClubApprovals = members.filter(m => 
        m.status === 'pending' || m.status === 'Pending'
      ).length;
      
      // Calculate pending program applications and programs without directors
      let pendingProgramApps = 0;
      let programsWithoutDirector = 0;
      
      for (const program of programsData) {
        try {
          // Check pending applications
          const appsRes = await fetch(`${backendUrl}/api/programs/${program._id}/applications?status=pending`, { headers });
          if (appsRes.ok) {
            const appsData = await appsRes.json();
            pendingProgramApps += appsData.applications?.length || appsData.length || 0;
          }
        } catch (e) {
          // Skip if applications endpoint not available
        }
        
        // Check for director in program members
        try {
          const membersRes = await fetch(`${backendUrl}/api/programs/${program._id}/members`, { headers });
          if (membersRes.ok) {
            const membersData = await membersRes.json();
            const membersList = Array.isArray(membersData) ? membersData : membersData.members || [];
            const hasDirector = membersList.some(m => 
              m.role === 'ProgramDirector' || m.role === 'programDirector'
            );
            if (!hasDirector) {
              programsWithoutDirector++;
            }
          } else {
            // If can't fetch members, check fallback properties
            if (!program.director && !program.programDirector) {
              programsWithoutDirector++;
            }
          }
        } catch (e) {
          // Fallback to checking program properties
          if (!program.director && !program.programDirector) {
            programsWithoutDirector++;
          }
        }
      }
      
      setDashboardData({
        club: clubData,
        programs: programsData,
        members: members,
        pendingClubApprovals,
        pendingProgramApps,
        pendingTeamInvites: 0, // TODO: Implement team invites count
        issues: programsWithoutDirector,
        loading: false
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(prev => ({ ...prev, loading: false }));
    }
  }, [selectedClubId, user]);

  // Fetch data when selected club changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchDashboardData(),
        refreshUserClubs?.()
      ]);
      showSuccess('Dashboard refreshed');
    } catch (error) {
      showError('Failed to refresh dashboard');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Club selection handler - persist to localStorage
  const handleClubSelect = (clubId) => {
    setSelectedClubId(clubId);
    setShowClubDropdown(false);
    localStorage.setItem('clubOwnerDashboard_selectedClub', clubId);
  };

  // Quick action handlers
  const handleAddProgram = () => {
    navigate(`/clubs/${selectedClubId}`);
  };

  const handleInviteMembers = () => {
    navigate(`/club/${selectedClubId}`, { state: { openModal: 'management', activeTab: 'members' } });
  };

  const handleAnnounce = () => {
    navigate(`/club/${selectedClubId}/notifications`);
  };

  const handleSettings = () => {
    navigate(`/club/${selectedClubId}/edit`);
  };

  // Empty state for no managed clubs
  if (managedClubs.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🏢</div>
          <h2>No Clubs to Manage</h2>
          <p>You don't have any clubs where you're an owner or manager.</p>
          <button className={styles.createClubButton} onClick={onExploreClick}>
            <FaPlus /> Create a Club
          </button>
        </div>
      </div>
    );
  }

  const totalActions = dashboardData.pendingClubApprovals + 
                       dashboardData.pendingProgramApps + 
                       dashboardData.pendingTeamInvites + 
                       dashboardData.issues;

  return (
    <div className={styles.container}>
      {/* Header with Refresh */}
      <div className={styles.headerBar}>
        <div className={styles.headerRight}>
          <button
            className={`${styles.refreshButton} ${isRefreshing ? styles.refreshing : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh dashboard"
          >
            <FaSyncAlt />
          </button>
        </div>
      </div>

      {/* Club Selector & Quick Actions */}
      <div className={styles.clubSelectorBar}>
        <div className={styles.clubSelector}>
          <button 
            className={styles.clubSelectorButton}
            onClick={() => setShowClubDropdown(!showClubDropdown)}
          >
            <div className={styles.clubInfo}>
              <span className={styles.clubLogo}>
                {selectedClub?.clubLogo ? (
                  <img src={selectedClub.clubLogo} alt="" />
                ) : (
                  '🏆'
                )}
              </span>
              <div className={styles.clubDetails}>
                <div className={styles.clubNameRow}>
                  <span className={styles.clubName}>{selectedClub?.name || 'Select Club'}</span>
                  {selectedClubId && (
                    <a
                      href={`${window.location.origin}/club/${selectedClubId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.viewClubLink}
                      onClick={(e) => e.stopPropagation()}
                      title="View Club Page"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  )}
                </div>
                <span className={styles.clubRole}>
                  <FaCrown className={styles.roleIcon} /> {userRole}
                </span>
              </div>
            </div>
            <FaChevronDown className={`${styles.dropdownIcon} ${showClubDropdown ? styles.open : ''}`} />
          </button>
          
          {showClubDropdown && (
            <div className={styles.clubDropdown}>
              {managedClubs.map((membership) => {
                const club = membership.club;
                const clubId = club?._id || club;
                const isSelected = String(clubId) === String(selectedClubId);
                
                return (
                  <button
                    key={clubId}
                    className={`${styles.clubOption} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleClubSelect(clubId)}
                  >
                    <span className={styles.optionLogo}>
                      {club?.clubLogo ? <img src={club.clubLogo} alt="" /> : '🏆'}
                    </span>
                    <div className={styles.optionDetails}>
                      <span className={styles.optionName}>{club?.name}</span>
                      <span className={styles.optionRole}>{membership.role}</span>
                    </div>
                    {isSelected && <FaCheck className={styles.checkIcon} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.quickActions}>
          <button className={styles.actionButton} onClick={handleAddProgram} title="Add Program">
            <FaPlus /> <span>Program</span>
          </button>
          <button className={styles.actionButton} onClick={handleInviteMembers} title="Invite Members">
            <FaUserPlus /> <span>Invite</span>
          </button>
          <button className={styles.actionButton} onClick={handleAnnounce} title="Send Announcement">
            <FaBullhorn /> <span>Announce</span>
          </button>
          <button className={styles.actionButton} onClick={handleSettings} title="Club Settings">
            <FaCog /> <span>Settings</span>
          </button>
        </div>
      </div>

      {/* Action Queue */}
      <ActionQueue
        clubId={selectedClubId}
        pendingClubApprovals={dashboardData.pendingClubApprovals}
        pendingProgramApps={dashboardData.pendingProgramApps}
        pendingTeamInvites={dashboardData.pendingTeamInvites}
        issues={dashboardData.issues}
        totalActions={totalActions}
        loading={dashboardData.loading}
        onRefresh={fetchDashboardData}
      />

      {/* Program Health Cards */}
      <ProgramHealthCards
        clubId={selectedClubId}
        programs={dashboardData.programs}
        loading={dashboardData.loading}
      />

      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          <ClubMembershipOverview
            clubId={selectedClubId}
            members={dashboardData.members}
            programs={dashboardData.programs}
            loading={dashboardData.loading}
          />
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          <ProgramStaffMatrix
            clubId={selectedClubId}
            programs={dashboardData.programs}
            loading={dashboardData.loading}
          />
        </div>
      </div>

      {/* Equipment Inventory (Full Width) */}
      <EquipmentInventory
        clubId={selectedClubId}
        programs={dashboardData.programs}
        loading={dashboardData.loading}
      />

      {/* Bottom Grid - Awards & Growth */}
      <div className={styles.bottomGrid}>
        <RecentAwardsWidget
          clubId={selectedClubId}
          programs={dashboardData.programs}
        />
        <ClubGrowthWidget
          clubId={selectedClubId}
          members={dashboardData.members}
          programs={dashboardData.programs}
        />
      </div>
    </div>
  );
};

export default ClubOwnerDashboard;

